// Company: what they do, the saved research's labelled lines (the Q4 lists' headline, voice fit,
// angle, ...), the strategists' Q4 research when the account is on a Q4 list, what's
// new (Scout's saved research, linked), funding with the
// investors on hover, usage, and the relationship -- AE and SDR owner, last touch, calls and
// pipeline. Every value is read off the row; a missing one shows as a short empty line.
import type { ReactNode } from 'react';
import type { BookAccount } from '../api/types';
import { lastOutbound } from '../book/derived';
import { fundingOf, fundShortName, formatUsd, readFunding, readRelationship, tieTooltip } from '../book/funding';
import { CardRows, CardTitle, HoverCard } from '../components/HoverCard';
import { formatCredits, formatShortDay } from '../lib/format';
import { overviewSource, researchDetailLines, safeHref, sourceHost } from './overviewSource';
import { compactNumber, highestPlan, openOpportunities } from './panelFacts';
import { PanelSection } from './panelParts';
import { Q4Research } from './Q4Research';
import { ScoresSection } from './ScoresSection';

/** One label / value line of a section. */
function FactLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="factline">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function WhatTheyDo({ row }: { row: BookAccount }) {
  const source = overviewSource(row);
  return (
    <PanelSection title="What they do" aside={source?.hint ?? null}>
      <p className="prose">{source ? source.text : 'No overview yet. Ask Scout to research it.'}</p>
    </PanelSection>
  );
}

/** The saved research's labelled lines and where voice fits: the Q4 lists' headline,
 * voice fit, angle and the rest. Nothing renders when the record has neither. */
function ResearchDetails({ row }: { row: BookAccount }) {
  const lines = researchDetailLines(row.company_research);
  if (!lines.length) return null;
  const strategist = row.company_research?.researched_by === 'strategist';
  return (
    <PanelSection title={strategist ? 'Strategist research' : 'Research notes'}>
      <dl className="factlines">
        {lines.map(line => <FactLine key={line.label} label={line.label}>{line.text}</FactLine>)}
      </dl>
    </PanelSection>
  );
}

function WhatsNew({ row }: { row: BookAccount }) {
  const research = row.company_research;
  // No saved research at all: "What they do" already says so, once.
  if (!research) return null;
  const news = research.whats_new;
  const sources = research.sources;
  if (!news.length && !sources.length) {
    return (
      <PanelSection title="What's new">
        <p className="panelempty">Nothing new when Scout last looked.</p>
      </PanelSection>
    );
  }
  return (
    <PanelSection title="What's new">
      <ul className="newslist">
        {news.map((item, index) => <li key={`news-${index}`}><span className="newstext">{item}</span></li>)}

        {/* Each cited source, dated and linked. Only http(s) links become hrefs. */}
        {sources.map((item, index) => {
          const href = safeHref(item.url);
          return (
            <li key={`source-${index}`}>
              <span className="newsday">{item.date ? formatShortDay(item.date) : ''}</span>
              <span className="newstext">{item.title}</span>
              {href ? <a href={href} target="_blank" rel="noopener noreferrer">{sourceHost(href)} ↗</a> : null}
            </li>
          );
        })}
      </ul>
    </PanelSection>
  );
}

function Funding({ row }: { row: BookAccount }) {
  const funding = readFunding(row);
  const relationship = readRelationship(row);
  const total = formatUsd(fundingOf(row)?.total_funding_usd);

  // Our funds on the cap table, with the rounds and the source on hover.
  const investorLabel = relationship.kind === 'funds'
    ? relationship.ties.map(tie => fundShortName(tie.fund)).join(', ')
    : relationship.kind === 'none' ? 'None of our funds' : 'Unknown';
  return (
    <PanelSection title="Funding">
      <dl className="factlines">
        {total ? <FactLine label="Total raised">{total}</FactLine> : null}
        <FactLine label="Last round">
          <HoverCard label="Funding source" card={() => <><CardTitle>{funding.label}</CardTitle><p className="hcnote">{funding.hint}</p></>}>
            <span className="dotted">{[funding.label, funding.detail].filter(Boolean).join(' · ')}</span>
          </HoverCard>
        </FactLine>
        <FactLine label="Investors">
          <HoverCard label="Cartesia investors" card={() => (
            <>
              <CardTitle>Cartesia investors</CardTitle>
              {relationship.ties.length
                ? <CardRows rows={relationship.ties.map(tie => [fundShortName(tie.fund), tieTooltip(tie)])} />
                : null}
              <p className="hcnote">{relationship.hint}</p>
            </>
          )}>
            <span className="dotted">{investorLabel}</span>
          </HoverCard>
        </FactLine>
      </dl>
    </PanelSection>
  );
}

function Usage({ row }: { row: BookAccount }) {
  const hasUsage = row.credits_30 != null || row.credits_prior_30 != null;
  const trend = row.credits_trend_pct;
  // The same highest-plan rollup the fact strip shows.
  const plan = highestPlan(row);
  return (
    <PanelSection title="Usage">
      {plan || hasUsage || row.orgs.length ? (
        <dl className="factlines">
          {plan ? <FactLine label="Plan"><span className={'tierchip' + (plan !== 'free' ? ' paid' : '')}>{plan}</span></FactLine> : null}
          {hasUsage ? (
            <FactLine label="Credits 30d">
              {formatCredits(row.credits_30)}
              {trend != null ? <span className={'trend ' + (trend > 0 ? 'up' : 'down')}> {trend > 0 ? '+' : ''}{Math.round(trend)}%</span> : null}
              {row.credits_prior_30 != null ? <span className="muted"> · was {compactNumber(row.credits_prior_30)}</span> : null}
            </FactLine>
          ) : null}
          {row.orgs.length ? <FactLine label="Orgs">{row.orgs.length} in the product · see History</FactLine> : null}
        </dl>
      ) : <p className="panelempty">No product usage on record.</p>}
    </PanelSection>
  );
}

function Relationship({ row }: { row: BookAccount }) {
  const last = lastOutbound(row);
  const open = openOpportunities(row);
  const latestCall = [...row.gong_calls].sort((first, second) => (second.day || '').localeCompare(first.day || ''))[0];
  const pipeline = open.map(deal => deal.qualified_pipeline).filter((amount): amount is number => amount != null)
    .reduce((sum, amount) => sum + amount, 0);
  return (
    <PanelSection title="Relationship">
      <dl className="factlines">
        <FactLine label="AE owner">{row.account_owner || 'None'}</FactLine>
        <FactLine label="SDR owner">{row.sdr_owner || 'Unassigned'}</FactLine>
        <FactLine label="Last out">
          {last ? <>{last.day}{last.who ? ` to ${last.who}` : ''}{last.author ? `, by ${last.author}` : ''}</> : 'Never'}
        </FactLine>
        <FactLine label="Calls">
          {row.gong_calls.length
            ? `${row.gong_calls.length} recorded · latest ${latestCall?.day ?? 'undated'}`
            : 'None recorded'}
          {row.meetings ? ` · ${row.meetings} meeting${row.meetings === 1 ? '' : 's'} held` : ''}
        </FactLine>
        <FactLine label="Pipeline">
          {open.length
            ? `${open.length} open · ${pipeline ? formatUsd(pipeline) : 'not quantified'} · ${open[0]?.stage ?? 'no stage'}`
            : 'No open deal'}
        </FactLine>
      </dl>
    </PanelSection>
  );
}

export function CompanyTab({ row, scoresVisible = false }: { row: BookAccount; scoresVisible?: boolean }) {
  return (
    <>
      <ScoresSection row={row} enabled={scoresVisible} />
      <WhatTheyDo row={row} />
      <ResearchDetails row={row} />
      <Q4Research row={row} />
      <WhatsNew row={row} />
      <Funding row={row} />
      <Usage row={row} />
      <Relationship row={row} />
      <p className="tabsource">Sources: Scout research, Q4 strategist research, Apollo funding, Salesforce, product usage.</p>
    </>
  );
}
